import * as XLSX from "xlsx";
import { Layout } from "@/components/layout/Layout";
import { api, TeamTimeEntry, PayPeriod, StaffUtilizationEntry, ActivityCode } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MetricCard } from "@/components/custom/MetricCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, parseISO, startOfMonth, startOfWeek, endOfWeek, subWeeks, subMonths, endOfMonth, startOfYear, endOfYear } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend
} from "recharts";
import { Download, Loader2, Printer, ShieldAlert, Pencil, Trash2, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/lib/authContext";
import { useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";

export default function Reports() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "admin";
  const search = useSearch();
  const { toast } = useToast();
  const initialTab = new URLSearchParams(search).get("tab") ?? "overview";

  // Role-gating: only managers and admins can access reports
  if (!isManager) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-3xl font-heading font-bold">Reports & Analytics</h1>
          <Card className="border-dashed border-2 border-destructive/50">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldAlert className="w-12 h-12 mb-4 text-destructive/50" />
              <p className="text-lg font-medium">Access Denied</p>
              <p className="text-sm">You need manager or administrator privileges to access reports.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Shared state
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Overview tab
  const [staffData, setStaffData] = useState<StaffUtilizationEntry[]>([]);
  const [overviewPeriodId, setOverviewPeriodId] = useState("");
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState("");

  // Payroll tab
  const [payrollPeriodId, setPayrollPeriodId] = useState("");
  const [payrollEntries, setPayrollEntries] = useState<TeamTimeEntry[]>([]);
  const [payrollLoading, setPayrollLoading] = useState(false);

  // Employee tab
  const [empUserId, setEmpUserId] = useState("");
  const [empPeriodId, setEmpPeriodId] = useState("");
  const [empEntries, setEmpEntries] = useState<TeamTimeEntry[]>([]);
  const [empLoading, setEmpLoading] = useState(false);

  // ── New report tabs: By Client, Delegation, Leverage ─────────────────────
  const isAdmin = user?.role === "admin";
  const _today = new Date();
  const [userRoles, setUserRoles] = useState<{ userId: string; userName: string; role: string }[]>([]);
  const [reportDateFrom, setReportDateFrom] = useState(format(startOfMonth(_today), "yyyy-MM-dd"));
  const [reportDateTo, setReportDateTo] = useState(format(_today, "yyyy-MM-dd"));
  const [reportEntries, setReportEntries] = useState<TeamTimeEntry[]>([]);
  const [reportUtilData, setReportUtilData] = useState<StaffUtilizationEntry[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [clientExpanded, setClientExpanded] = useState<Set<string>>(new Set());
  const [delegExpanded, setDelegExpanded] = useState<Set<string>>(new Set());
  const [delegExtraUserIds, setDelegExtraUserIds] = useState<Set<string>>(new Set());
  const [excludedCategories, setExcludedCategories] = useState<Set<string>>(new Set());
  const LEVERAGE_RATES_KEY = "centcom-leverage-rates";
  const defaultRates: Record<string, number> = { admin: 250, manager: 175, staff: 95, contractor: 75 };
  const [leverageRates, setLeverageRates] = useState<Record<string, number>>(() => {
    try { return { ...defaultRates, ...JSON.parse(localStorage.getItem(LEVERAGE_RATES_KEY) ?? "{}") }; }
    catch { return defaultRates; }
  });

  // Employee tab — inline edit/delete
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ entry_date: "", hours: "", account_id: "", activity_code: "", notes: "", billable: false });
  const [empSaving, setEmpSaving] = useState(false);
  const [empDeleting, setEmpDeleting] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);
  const [editAccounts, setEditAccounts] = useState<{ id: string; name: string }[]>([]);
  const [editRefDataLoaded, setEditRefDataLoaded] = useState(false);

  const loadStaff = useCallback(async (start: string, end: string) => {
    setStaffLoading(true);
    setStaffError("");
    try {
      const data = await api.getStaffUtilization(start, end);
      setStaffData(data);
    } catch (e: any) {
      console.error(e);
      setStaffError(e?.message || "Failed to load utilization data");
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const loadPayroll = useCallback(async (periodId: string) => {
    const period = payPeriods.find(p => p.id === periodId);
    if (!period) return;
    setPayrollLoading(true);
    try {
      const data = await api.getTeamTimeEntries({ startDate: period.start_date, endDate: period.end_date });
      setPayrollEntries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setPayrollLoading(false);
    }
  }, [payPeriods]);

  const loadEmployee = useCallback(async (userId: string, periodId: string) => {
    const period = payPeriods.find(p => p.id === periodId);
    if (!period || !userId) return;
    setEmpLoading(true);
    try {
      const data = await api.getTeamTimeEntries({ startDate: period.start_date, endDate: period.end_date, userId });
      setEmpEntries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setEmpLoading(false);
    }
  }, [payPeriods]);

  useEffect(() => {
    const init = async () => {
      try {
        const [periodsData, usersData, rolesData] = await Promise.all([
          api.getPayPeriods(),
          api.getUsers(),
          api.getUserRoles(),
        ]);
        setUserRoles(rolesData);
        const sorted = periodsData.sort((a, b) =>
          new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
        );
        // Filter to current + past periods only (exclude future) for dropdown
        const today = new Date().toISOString().slice(0, 10);
        const pastAndCurrent = sorted.filter(p => p.start_date <= today);
        setPayPeriods(pastAndCurrent);
        setUsers(usersData.map((u: any) => ({ id: u.id, name: u.name })));

        // Default to the period containing today, then most recently ended
        const current = pastAndCurrent.find(p => p.start_date <= today && p.end_date >= today)
          || pastAndCurrent[0];
        if (current) {
          setOverviewPeriodId(current.id);
          setPayrollPeriodId(current.id);
          setEmpPeriodId(current.id);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isManager || !overviewPeriodId) return;
    const p = payPeriods.find(x => x.id === overviewPeriodId);
    if (p) loadStaff(p.start_date, p.end_date);
  }, [overviewPeriodId, payPeriods, isManager, loadStaff]);

  useEffect(() => {
    if (!isManager || !payrollPeriodId) return;
    loadPayroll(payrollPeriodId);
  }, [payrollPeriodId, isManager, loadPayroll]);

  useEffect(() => {
    if (!isManager || !empUserId || !empPeriodId) return;
    loadEmployee(empUserId, empPeriodId);
  }, [empUserId, empPeriodId, isManager, loadEmployee]);

  // ── Derived overview metrics ──────────────────────────────────────────────
  const totalBillable = staffData.reduce((s, r) => s + r.billable_hours, 0);
  const totalNonBillable = staffData.reduce((s, r) => s + r.non_billable_hours, 0);
  const totalPTO = staffData.reduce((s, r) => s + r.pto_hours, 0);
  const totalHours = totalBillable + totalNonBillable + totalPTO;
  const firmUtilization = (totalBillable + totalNonBillable) > 0
    ? (totalBillable / (totalBillable + totalNonBillable)) * 100 : 0;

  const pieData = [
    { name: "Billable", value: totalBillable, color: "hsl(var(--chart-1))" },
    { name: "Non-Billable", value: totalNonBillable, color: "hsl(var(--chart-5))" },
    { name: "PTO/Leave", value: totalPTO, color: "hsl(var(--chart-2))" },
  ].filter(d => d.value > 0);

  // ── Payroll helpers ───────────────────────────────────────────────────────
  const payrollByUser = payrollEntries.reduce<Record<string, TeamTimeEntry[]>>((acc, e) => {
    if (!acc[e.user_id]) acc[e.user_id] = [];
    acc[e.user_id].push(e);
    return acc;
  }, {});

  const printPayroll = () => {
    const period = payPeriods.find(p => p.id === payrollPeriodId);
    const periodStr = period ? periodLabel(period) : "";
    const payDateStr = period ? format(parseISO(period.pay_date), "MMMM d, yyyy") : "";
    const printedAt = format(new Date(), "MMMM d, yyyy 'at' h:mm a");

    const employeeBlocks = Object.entries(payrollByUser).map(([, entries]) => {
      const totalH = entries.reduce((s, e) => s + e.hours, 0);
      const billableH = entries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
      const byDate = entries.reduce<Record<string, TeamTimeEntry[]>>((acc, e) => {
        if (!acc[e.entry_date]) acc[e.entry_date] = [];
        acc[e.entry_date].push(e);
        return acc;
      }, {});

      const rows = Object.entries(byDate).map(([date, dayEntries]) => {
        const dayTotal = dayEntries.reduce((s, e) => s + e.hours, 0);
        const entryRows = dayEntries.map((e, i) => `
          <tr>
            <td class="date">${i === 0 ? format(parseISO(date), "EEE MM/dd") : ""}</td>
            <td>${e.account_name || "—"}</td>
            <td class="code">${e.activity_code}</td>
            <td>${e.activity_name}</td>
            <td class="num">${e.hours.toFixed(2)}</td>
            <td class="num">${e.billable ? "●" : "○"}</td>
            <td class="note">${e.notes ?? ""}</td>
          </tr>`).join("");
        return entryRows + `
          <tr class="day-sub">
            <td colspan="4" class="day-label">Day total</td>
            <td class="num">${dayTotal.toFixed(2)}</td>
            <td colspan="2"></td>
          </tr>`;
      }).join("");

      return `
        <div class="employee">
          <div class="emp-header">
            <span class="emp-name">${entries[0].user_name}</span>
            <span class="emp-summary">${billableH.toFixed(1)}h billable &nbsp;/&nbsp; ${totalH.toFixed(1)}h total</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Client</th><th>Code</th><th>Activity</th>
                <th class="num">Hours</th><th class="num">Bill</th><th>Notes</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
            <tfoot>
              <tr class="period-total">
                <td colspan="4" class="day-label">Period total</td>
                <td class="num">${totalH.toFixed(2)}</td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>`;
    }).join("");

    const grandTotal = payrollEntries.reduce((s, e) => s + e.hours, 0);
    const grandBillable = payrollEntries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Payroll Report — ${periodStr}</title>
  <style>
    @page { margin: 0.5in 0.6in; size: letter landscape; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 9.5pt; color: #111; }

    .report-header { border-bottom: 2px solid #1a1a2e; padding-bottom: 10px; margin-bottom: 18px; }
    .firm-name { font-size: 14pt; font-weight: bold; }
    .report-title { font-size: 11pt; font-weight: 600; margin-top: 2px; }
    .report-meta { font-size: 8.5pt; color: #555; margin-top: 4px; }

    .employee { margin-bottom: 22px; break-inside: avoid-page; }
    .emp-header { display: flex; justify-content: space-between; align-items: baseline;
                  background: #f0f0f0; padding: 5px 8px; border: 1px solid #ccc;
                  border-bottom: none; break-after: avoid; }
    .emp-name { font-size: 11pt; font-weight: bold; }
    .emp-summary { font-size: 8.5pt; color: #555; }

    table { width: 100%; border-collapse: collapse; }
    th { background: #e8e8e8; font-size: 8pt; font-weight: 600; text-align: left;
         padding: 4px 6px; border: 1px solid #ccc; white-space: nowrap; }
    td { padding: 3px 6px; border: 1px solid #ddd; vertical-align: top; white-space: nowrap; }
    tr:nth-child(even) td { background: #fafafa; }

    td.date { white-space: nowrap; font-weight: 500; width: 68px; }
    td.code { font-family: monospace; font-size: 8pt; width: 44px; }
    td.num { text-align: right; font-weight: 600; width: 52px; }
    td.note { color: #555; max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
    td.day-label { text-align: right; font-style: italic; font-size: 8pt; color: #555; }

    tr.day-sub td { background: #efefef; border-top: 1px solid #bbb; }
    tr.period-total td { background: #e0e0e0; font-weight: bold; border-top: 2px solid #999; }

    .grand-total { margin-top: 16px; border: 2px solid #1a1a2e; padding: 8px 12px;
                   display: flex; justify-content: space-between; font-weight: bold; font-size: 10.5pt; }
  </style>
</head>
<body>
  <div class="report-header">
    <div class="firm-name">Carpenter &amp; Company, CPAs, PC</div>
    <div class="report-title">Payroll Report</div>
    <div class="report-meta">
      Pay Period: ${periodStr}${payDateStr ? ` &nbsp;·&nbsp; Pay Date: ${payDateStr}` : ""}
      &nbsp;·&nbsp; Printed ${printedAt}
    </div>
  </div>
  ${employeeBlocks}
  <div class="grand-total">
    <span>All Staff — Period Total</span>
    <span>${grandBillable.toFixed(2)}h billable &nbsp;/&nbsp; ${grandTotal.toFixed(2)}h total</span>
  </div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=850,height=1100");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const exportPayrollCSV = () => {
    const header = ["Date", "Employee", "Client", "Code", "Activity", "Hours", "Billable", "Notes"];
    const rows = payrollEntries.map(e => [
      e.entry_date, e.user_name, e.account_name, e.activity_code,
      e.activity_name, e.hours, e.billable ? "Yes" : "No", e.notes ?? "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 30 }, { wch: 8 }, { wch: 25 }, { wch: 8 }, { wch: 10 }, { wch: 40 }];
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payroll");
    XLSX.writeFile(wb, `payroll-${payrollPeriodId}.xlsx`);
  };

  const exportEmpCSV = () => {
    const emp = users.find(u => u.id === empUserId);
    const header = ["Date", "Client", "Code", "Activity", "Hours", "Billable", "Notes"];
    const rows = empEntries.map(e => [
      e.entry_date, e.account_name, e.activity_code,
      e.activity_name, e.hours, e.billable ? "Yes" : "No", e.notes ?? "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws["!cols"] = [{ wch: 12 }, { wch: 30 }, { wch: 8 }, { wch: 25 }, { wch: 8 }, { wch: 10 }, { wch: 40 }];
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Timesheet");
    XLSX.writeFile(wb, `timesheet-${emp?.name ?? empUserId}-${empPeriodId}.xlsx`);
  };

  const periodLabel = (p: PayPeriod) =>
    `${format(parseISO(p.start_date), "MMM d")} – ${format(parseISO(p.end_date), "MMM d, yyyy")}`;

  const loadReportData = useCallback(async () => {
    setReportLoading(true);
    try {
      const [entries, util] = await Promise.all([
        api.getTeamTimeEntries({ startDate: reportDateFrom, endDate: reportDateTo }),
        api.getStaffUtilization(reportDateFrom, reportDateTo),
      ]);
      setReportEntries(entries);
      setReportUtilData(util);
    } catch (e) {
      console.error(e);
    } finally {
      setReportLoading(false);
    }
  }, [reportDateFrom, reportDateTo]);

  useEffect(() => {
    if (!isManager) return;
    loadReportData();
  }, [reportDateFrom, reportDateTo, isManager, loadReportData]);

  // ── Derived: available categories from loaded entries ────────────────────
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    reportEntries.forEach(e => { if (e.activity_category) cats.add(e.activity_category); });
    return Array.from(cats).sort();
  }, [reportEntries]);

  // Entries with excluded categories stripped out (shared filter for By Client + Delegation)
  const filteredReportEntries = useMemo(
    () => excludedCategories.size === 0
      ? reportEntries
      : reportEntries.filter(e => !excludedCategories.has(e.activity_category)),
    [reportEntries, excludedCategories]
  );

  // ── Derived: Time by Client ───────────────────────────────────────────────
  const clientGrouped = useMemo(() => {
    const map: Record<string, {
      accountId: string; accountName: string; totalHours: number; billableHours: number;
      employees: Record<string, { name: string; total: number; billable: number }>;
    }> = {};
    filteredReportEntries.forEach(e => {
      const key = e.account_id ?? "__none__";
      if (!map[key]) map[key] = { accountId: key, accountName: e.account_name || "(No Client)", totalHours: 0, billableHours: 0, employees: {} };
      map[key].totalHours += e.hours;
      if (e.billable) map[key].billableHours += e.hours;
      if (!map[key].employees[e.user_id]) map[key].employees[e.user_id] = { name: e.user_name, total: 0, billable: 0 };
      map[key].employees[e.user_id].total += e.hours;
      if (e.billable) map[key].employees[e.user_id].billable += e.hours;
    });
    return Object.values(map).sort((a, b) => b.totalHours - a.totalHours);
  }, [filteredReportEntries]);

  // ── Derived: Delegation ───────────────────────────────────────────────────
  const contractorUserIds = useMemo(
    () => new Set(userRoles.filter(u => u.role === "contractor").map(u => u.userId)),
    [userRoles]
  );
  const delegUserIds = useMemo(() => {
    const ids = new Set(contractorUserIds);
    delegExtraUserIds.forEach(id => ids.add(id));
    return ids;
  }, [contractorUserIds, delegExtraUserIds]);
  const delegGrouped = useMemo(() => {
    // Group by client; for each client split hours into "delegated" vs "senior"
    const map: Record<string, {
      accountId: string; accountName: string;
      seniorHours: number; delegatedHours: number; totalHours: number;
      people: Record<string, { name: string; isDelegated: boolean; hours: number }>;
    }> = {};
    filteredReportEntries.forEach(e => {
      const ck = e.account_id ?? "__none__";
      if (!map[ck]) map[ck] = { accountId: ck, accountName: e.account_name || "(No Client)", seniorHours: 0, delegatedHours: 0, totalHours: 0, people: {} };
      const isDelegated = delegUserIds.has(e.user_id);
      map[ck].totalHours += e.hours;
      isDelegated ? (map[ck].delegatedHours += e.hours) : (map[ck].seniorHours += e.hours);
      if (!map[ck].people[e.user_id]) map[ck].people[e.user_id] = { name: e.user_name, isDelegated, hours: 0 };
      map[ck].people[e.user_id].hours += e.hours;
    });
    return Object.values(map)
      .filter(c => c.totalHours > 0)
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [filteredReportEntries, delegUserIds]);

  // ── Derived: Leverage ─────────────────────────────────────────────────────
  const leverageRows = useMemo(() => {
    const roleMap: Record<string, string> = {};
    userRoles.forEach(u => { roleMap[u.userId] = u.role; });
    return reportUtilData.map(s => {
      const role = roleMap[s.user_id] ?? "staff";
      const rate = leverageRates[role] ?? 0;
      const estRevenue = s.billable_hours * rate;
      const effectiveRate = s.total_hours > 0 ? estRevenue / s.total_hours : 0;
      return { ...s, role, rate, estRevenue, effectiveRate };
    }).sort((a, b) => b.estRevenue - a.estRevenue);
  }, [reportUtilData, userRoles, leverageRates]);

  // ── Date presets for new tabs ─────────────────────────────────────────────
  const reportPresets = [
    { label: "This Week", from: startOfWeek(_today, { weekStartsOn: 1 }), to: endOfWeek(_today, { weekStartsOn: 1 }) },
    { label: "Last Week", from: startOfWeek(subWeeks(_today, 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(_today, 1), { weekStartsOn: 1 }) },
    { label: "This Month", from: startOfMonth(_today), to: endOfMonth(_today) },
    { label: "Last Month", from: startOfMonth(subMonths(_today, 1)), to: endOfMonth(subMonths(_today, 1)) },
    { label: "This Year", from: startOfYear(_today), to: endOfYear(_today) },
  ];

  // ── Print: By Client ──────────────────────────────────────────────────────
  const printClientReport = () => {
    const clientBlocks = clientGrouped.map(c => {
      const pct = c.totalHours > 0 ? Math.round((c.billableHours / c.totalHours) * 100) : 0;
      const empRows = Object.values(c.employees)
        .sort((a, b) => b.total - a.total)
        .map(e => {
          const ep = e.total > 0 ? Math.round((e.billable / e.total) * 100) : 0;
          return `<tr><td class="indent">${e.name}</td><td class="num">${e.billable.toFixed(2)}</td><td class="num">${e.total.toFixed(2)}</td><td class="num">${ep}%</td></tr>`;
        }).join("");
      return `<tr class="client-row"><td><strong>${c.accountName}</strong></td><td class="num">${c.billableHours.toFixed(2)}</td><td class="num">${c.totalHours.toFixed(2)}</td><td class="num">${pct}%</td></tr>${empRows}`;
    }).join("");
    const grandTotal = reportEntries.reduce((s, e) => s + e.hours, 0);
    const grandBillable = reportEntries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Time by Client — ${reportDateFrom} to ${reportDateTo}</title>
<style>@page{margin:.5in .6in;size:letter portrait}*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:9.5pt;color:#111}
.hdr{border-bottom:2px solid #1a1a2e;padding-bottom:8px;margin-bottom:14px}
.firm{font-size:14pt;font-weight:bold}.title{font-size:11pt;font-weight:600;margin-top:2px}
.meta{font-size:8.5pt;color:#555;margin-top:3px}
table{width:100%;border-collapse:collapse}
th{background:#e8e8e8;font-size:8pt;font-weight:600;text-align:left;padding:4px 6px;border:1px solid #ccc}
td{padding:3px 6px;border:1px solid #ddd;vertical-align:top}
.num{text-align:right;font-weight:600;width:80px}
.client-row td{background:#f0f4ff;font-weight:bold;border-top:2px solid #aab}
td.indent{padding-left:20px}
.total-row td{background:#e0e0e0;font-weight:bold;border-top:2px solid #999}
</style></head><body>
<div class="hdr"><div class="firm">Carpenter &amp; Company, CPAs, PC</div>
<div class="title">Time by Client Report</div>
<div class="meta">${reportDateFrom} to ${reportDateTo} &nbsp;·&nbsp; Printed ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}</div></div>
<table><thead><tr><th>Client / Employee</th><th class="num">Billable Hrs</th><th class="num">Total Hrs</th><th class="num">Bill %</th></tr></thead>
<tbody>${clientBlocks}</tbody>
<tfoot><tr class="total-row"><td>FIRM TOTAL</td><td class="num">${grandBillable.toFixed(2)}</td><td class="num">${grandTotal.toFixed(2)}</td><td class="num">${grandTotal > 0 ? Math.round((grandBillable / grandTotal) * 100) : 0}%</td></tr></tfoot>
</table></body></html>`;
    const win = window.open("", "_blank", "width=750,height=1050");
    if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const exportClientCSV = () => {
    const rows: any[][] = [["Client", "Employee", "Billable Hrs", "Total Hrs", "Billable %"]];
    clientGrouped.forEach(c => {
      rows.push([c.accountName, "", c.billableHours.toFixed(2), c.totalHours.toFixed(2),
        c.totalHours > 0 ? Math.round((c.billableHours / c.totalHours) * 100) + "%" : "0%"]);
      Object.values(c.employees).sort((a, b) => b.total - a.total).forEach(e => {
        rows.push(["", e.name, e.billable.toFixed(2), e.total.toFixed(2),
          e.total > 0 ? Math.round((e.billable / e.total) * 100) + "%" : "0%"]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 35 }, { wch: 25 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "By Client");
    XLSX.writeFile(wb, `time-by-client-${reportDateFrom}-${reportDateTo}.xlsx`);
  };

  // ── Print: Delegation ─────────────────────────────────────────────────────
  const printDelegationReport = () => {
    const clientBlocks = delegGrouped.map(c => {
      const delegPct = c.totalHours > 0 ? Math.round((c.delegatedHours / c.totalHours) * 100) : 0;
      const peopleRows = Object.values(c.people).sort((a, b) => b.hours - a.hours).map(p =>
        `<tr><td class="indent">${p.name}${p.isDelegated ? ' <span class="badge">delegated</span>' : ''}</td><td class="num">${p.hours.toFixed(2)}</td><td class="num"></td><td class="num"></td></tr>`
      ).join("");
      return `<tr class="client-row"><td><strong>${c.accountName}</strong></td><td class="num">${c.seniorHours.toFixed(2)}</td><td class="num">${c.delegatedHours.toFixed(2)}</td><td class="num pct">${delegPct}%</td></tr>${peopleRows}`;
    }).join("");
    const totSenior = delegGrouped.reduce((s, c) => s + c.seniorHours, 0);
    const totDeleg = delegGrouped.reduce((s, c) => s + c.delegatedHours, 0);
    const totAll = totSenior + totDeleg;
    const firmPct = totAll > 0 ? Math.round((totDeleg / totAll) * 100) : 0;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Delegation Ratio — ${reportDateFrom} to ${reportDateTo}</title>
<style>@page{margin:.5in .6in;size:letter portrait}*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:9.5pt;color:#111}
.hdr{border-bottom:2px solid #1a1a2e;padding-bottom:8px;margin-bottom:14px}
.firm{font-size:14pt;font-weight:bold}.title{font-size:11pt;font-weight:600;margin-top:2px}
.meta{font-size:8.5pt;color:#555;margin-top:3px}
table{width:100%;border-collapse:collapse}
th{background:#e8e8e8;font-size:8pt;font-weight:600;text-align:left;padding:4px 6px;border:1px solid #ccc}
td{padding:3px 6px;border:1px solid #ddd;vertical-align:top}
.num{text-align:right;font-weight:600;width:80px}
.pct{color:#7c3aed}
.client-row td{background:#f0f4ff;font-weight:bold;border-top:2px solid #aab}
td.indent{padding-left:20px}
.badge{font-size:7pt;background:#ede9fe;color:#7c3aed;border-radius:3px;padding:1px 4px;font-weight:normal}
.total-row td{background:#e0e0e0;font-weight:bold;border-top:2px solid #999}
</style></head><body>
<div class="hdr"><div class="firm">Carpenter &amp; Company, CPAs, PC</div>
<div class="title">Delegation Ratio by Client</div>
<div class="meta">${reportDateFrom} to ${reportDateTo} &nbsp;·&nbsp; Printed ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}</div></div>
<table><thead><tr><th>Client / Staff</th><th class="num">Senior Hrs</th><th class="num">Delegated Hrs</th><th class="num pct">Delegated %</th></tr></thead>
<tbody>${clientBlocks}</tbody>
<tfoot><tr class="total-row"><td>FIRM TOTAL</td><td class="num">${totSenior.toFixed(2)}</td><td class="num">${totDeleg.toFixed(2)}</td><td class="num pct">${firmPct}%</td></tr></tfoot>
</table></body></html>`;
    const win = window.open("", "_blank", "width=750,height=1050");
    if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const exportDelegationCSV = () => {
    const rows: any[][] = [["Client", "Senior Hrs", "Delegated Hrs", "Total Hrs", "Delegated %", "Staff Member", "Is Delegated", "Hours"]];
    delegGrouped.forEach(c => {
      const delegPct = c.totalHours > 0 ? Math.round((c.delegatedHours / c.totalHours) * 100) + "%" : "0%";
      rows.push([c.accountName, c.seniorHours.toFixed(2), c.delegatedHours.toFixed(2), c.totalHours.toFixed(2), delegPct, "", "", ""]);
      Object.values(c.people).sort((a, b) => b.hours - a.hours).forEach(p => {
        rows.push(["", "", "", "", "", p.name, p.isDelegated ? "Yes" : "No", p.hours.toFixed(2)]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 35 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 13 }, { wch: 25 }, { wch: 13 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Delegation Ratio");
    XLSX.writeFile(wb, `delegation-ratio-${reportDateFrom}-${reportDateTo}.xlsx`);
  };

  // ── Print: Leverage ───────────────────────────────────────────────────────
  const printLeverageReport = () => {
    const totalBill = leverageRows.reduce((s, r) => s + r.billable_hours, 0);
    const totalRev = leverageRows.reduce((s, r) => s + r.estRevenue, 0);
    const totalHrsAll = leverageRows.reduce((s, r) => s + r.total_hours, 0);
    const tableRows = leverageRows.map(r => `
<tr><td>${r.name}</td><td class="cap">${r.role}</td><td class="num">${r.billable_hours.toFixed(1)}h</td>
<td class="num">$${r.rate}/hr</td><td class="num">$${r.estRevenue.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
<td class="num">${r.non_billable_hours.toFixed(1)}h</td><td class="num">${r.total_hours.toFixed(1)}h</td>
<td class="num">$${r.effectiveRate.toFixed(0)}/hr</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Leverage Report — ${reportDateFrom} to ${reportDateTo}</title>
<style>@page{margin:.5in .6in;size:letter landscape}*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:9.5pt;color:#111}
.hdr{border-bottom:2px solid #1a1a2e;padding-bottom:8px;margin-bottom:14px}
.firm{font-size:14pt;font-weight:bold}.title{font-size:11pt;font-weight:600;margin-top:2px}
.meta{font-size:8.5pt;color:#555;margin-top:3px}
.rates{font-size:8pt;color:#666;margin-bottom:10px}
table{width:100%;border-collapse:collapse}
th{background:#e8e8e8;font-size:8pt;font-weight:600;text-align:left;padding:4px 6px;border:1px solid #ccc}
td{padding:3px 6px;border:1px solid #ddd}
.num{text-align:right;font-weight:600}.cap{text-transform:capitalize}
.total-row td{background:#e0e0e0;font-weight:bold;border-top:2px solid #999}
.note{font-size:7.5pt;color:#888;margin-top:10px;font-style:italic}
</style></head><body>
<div class="hdr"><div class="firm">Carpenter &amp; Company, CPAs, PC</div>
<div class="title">Leverage / Profitability Report (Estimated)</div>
<div class="meta">${reportDateFrom} to ${reportDateTo} &nbsp;·&nbsp; Printed ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}</div></div>
<div class="rates">Rates: Admin $${leverageRates.admin}/hr &nbsp;|&nbsp; Manager $${leverageRates.manager}/hr &nbsp;|&nbsp; Staff $${leverageRates.staff}/hr &nbsp;|&nbsp; Contractor $${leverageRates.contractor}/hr</div>
<table><thead><tr><th>Name</th><th>Role</th><th class="num">Bill. Hrs</th><th class="num">Rate</th><th class="num">Est. Revenue</th><th class="num">Non-Bill</th><th class="num">Total Hrs</th><th class="num">Eff. Rate</th></tr></thead>
<tbody>${tableRows}</tbody>
<tfoot><tr class="total-row"><td colspan="2">FIRM TOTAL</td><td class="num">${totalBill.toFixed(1)}h</td><td class="num">—</td><td class="num">$${totalRev.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td><td class="num">—</td><td class="num">${totalHrsAll.toFixed(1)}h</td><td class="num">${totalHrsAll > 0 ? "$" + (totalRev / totalHrsAll).toFixed(0) + "/hr" : "—"}</td></tr></tfoot>
</table>
<div class="note">* Revenue estimates only. Actual billed amounts may differ. Connect billing data for actuals.</div>
</body></html>`;
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const exportLeverageCSV = () => {
    const header = ["Name", "Role", "Billable Hrs", "Rate ($/hr)", "Est. Revenue", "Non-Billable Hrs", "Total Hrs", "Eff. Rate ($/hr)"];
    const rows = leverageRows.map(r => [
      r.name, r.role, r.billable_hours.toFixed(2), r.rate,
      r.estRevenue.toFixed(2), r.non_billable_hours.toFixed(2), r.total_hours.toFixed(2), r.effectiveRate.toFixed(2),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws["!cols"] = [{ wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 12 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leverage");
    XLSX.writeFile(wb, `leverage-${reportDateFrom}-${reportDateTo}.xlsx`);
  };

  const loadEditRefData = async () => {
    if (editRefDataLoaded) return;
    const [codes, accts] = await Promise.all([
      api.getActivityCodes().catch(() => [] as ActivityCode[]),
      api.getAccounts().catch(() => []),
    ]);
    setActivityCodes(codes.filter((c: ActivityCode) => c.is_active));
    setEditAccounts(accts.map((a: any) => ({ id: a.id, name: a.account_name })));
    setEditRefDataLoaded(true);
  };

  const startEdit = async (e: TeamTimeEntry) => {
    await loadEditRefData();
    setEditForm({
      entry_date: e.entry_date,
      hours: e.hours.toFixed(2),
      account_id: e.account_id ?? "",
      activity_code: e.activity_code,
      notes: e.notes ?? "",
      billable: e.billable,
    });
    setConfirmDeleteId(null);
    setEditingId(e.id);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async () => {
    if (!editingId) return;
    setEmpSaving(true);
    try {
      await api.updateTimeEntry(editingId, {
        entry_date: editForm.entry_date,
        minutes: Math.round(parseFloat(editForm.hours) * 60),
        account_id: editForm.account_id || null,
        activity_code: editForm.activity_code,
        notes: editForm.notes,
        billable: editForm.billable,
      });
      const code = activityCodes.find(c => c.code === editForm.activity_code);
      const acct = editAccounts.find(a => a.id === editForm.account_id);
      setEmpEntries(prev => prev.map(e => e.id === editingId ? {
        ...e,
        entry_date: editForm.entry_date,
        hours: parseFloat(editForm.hours),
        account_id: editForm.account_id,
        account_name: acct?.name ?? e.account_name,
        activity_code: editForm.activity_code,
        activity_name: code?.name ?? e.activity_name,
        notes: editForm.notes,
        billable: editForm.billable,
      } : e));
      setEditingId(null);
    } catch (err: any) {
      toast({ title: "Action failed", description: err?.message ?? "Failed to save — entry may be approved or in a closed period.", variant: "destructive" });
    } finally {
      setEmpSaving(false);
    }
  };

  const deleteEntry = async (id: string) => {
    setEmpDeleting(id);
    try {
      await api.deleteTimeEntry(id);
      setEmpEntries(prev => prev.filter(e => e.id !== id));
      setConfirmDeleteId(null);
    } catch (err: any) {
      toast({ title: "Action failed", description: err?.message ?? "Cannot delete — entry may not be in draft status.", variant: "destructive" });
    } finally {
      setEmpDeleting(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <div className="print:hidden">
          <h1 className="text-3xl font-heading font-bold tracking-tight" data-testid="text-page-title">
            Reports & Analytics
          </h1>
          <p className="text-muted-foreground">Firm-wide time and utilization reporting</p>
        </div>

        <Tabs defaultValue={initialTab}>
          <TabsList className="mb-2 print:hidden flex-wrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {isManager && <TabsTrigger value="payroll">Payroll Report</TabsTrigger>}
            {isManager && <TabsTrigger value="employee">Employee Report</TabsTrigger>}
            {isManager && <TabsTrigger value="by-client">By Client</TabsTrigger>}
            {isManager && <TabsTrigger value="delegation">Delegation</TabsTrigger>}
            {isAdmin && <TabsTrigger value="leverage">Leverage</TabsTrigger>}
          </TabsList>

          {/* ── OVERVIEW TAB ── */}
          <TabsContent value="overview" className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Pay Period:</span>
              <Select value={overviewPeriodId} onValueChange={setOverviewPeriodId}>
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {payPeriods.map(p => (
                    <SelectItem key={p.id} value={p.id}>{periodLabel(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {staffError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {staffError}
              </div>
            )}

            {staffLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard title="Firm Utilization" value={`${firmUtilization.toFixed(1)}%`} className="border-l-4 border-l-primary" />
                  <MetricCard title="Billable Hours" value={totalBillable.toFixed(1)} subValue="Hours" className="border-l-4 border-l-emerald-500" />
                  <MetricCard title="Total Hours" value={totalHours.toFixed(1)} subValue="Hours" className="border-l-4 border-l-sidebar-primary" />
                  <MetricCard title="PTO Taken" value={totalPTO.toFixed(1)} subValue="Hours" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card className="lg:col-span-2 shadow-md">
                    <CardHeader>
                      <CardTitle>Staff Utilization</CardTitle>
                      <CardDescription>Billable rate per team member</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {staffData.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No time entries for this period.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-muted-foreground text-left">
                                <th className="pb-2 font-medium">Name</th>
                                <th className="pb-2 font-medium text-right">Billable</th>
                                <th className="pb-2 font-medium text-right">Non-Bill</th>
                                <th className="pb-2 font-medium text-right">PTO</th>
                                <th className="pb-2 font-medium text-right">Total</th>
                                <th className="pb-2 font-medium pl-4">Rate</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {staffData.map(s => {
                                const pct = s.utilization_pct;
                                const badge = pct >= 80
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"
                                  : pct >= 60
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
                                return (
                                  <tr key={s.user_id}>
                                    <td className="py-3 font-medium">{s.name}</td>
                                    <td className="py-3 text-right font-mono">{s.billable_hours.toFixed(1)}h</td>
                                    <td className="py-3 text-right font-mono text-muted-foreground">{s.non_billable_hours.toFixed(1)}h</td>
                                    <td className="py-3 text-right font-mono text-muted-foreground">{s.pto_hours.toFixed(1)}h</td>
                                    <td className="py-3 text-right font-mono font-semibold">{s.total_hours.toFixed(1)}h</td>
                                    <td className="py-3 pl-4">
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden min-w-[60px]">
                                          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                                        </div>
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${badge}`}>
                                          {pct.toFixed(1)}%
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="shadow-md">
                    <CardHeader>
                      <CardTitle>Hour Distribution</CardTitle>
                      <CardDescription>Firm-wide breakdown</CardDescription>
                    </CardHeader>
                    <CardContent className="h-[300px]">
                      {pieData.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-muted-foreground">No data.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                            <Tooltip formatter={(v: number) => [`${v.toFixed(2)}h`, "Hours"]} />
                            <Legend verticalAlign="bottom" height={36} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {staffData.length > 0 && (
                  <Card className="shadow-md">
                    <CardHeader>
                      <CardTitle>Utilization by Person</CardTitle>
                    </CardHeader>
                    <CardContent className="h-[260px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={staffData.map(s => ({ name: s.name, billable: s.billable_hours, nonBillable: s.non_billable_hours }))} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v: number) => [`${v.toFixed(1)}h`]} />
                          <Legend />
                          <Bar dataKey="billable" name="Billable" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="nonBillable" name="Non-Billable" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ── PAYROLL REPORT TAB ── */}
          {isManager && (
            <TabsContent value="payroll" className="space-y-6">
              {/* Print-only report header */}
              {(() => {
                const selectedPeriod = payPeriods.find(p => p.id === payrollPeriodId);
                return (
                  <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-800">
                    <div className="text-xl font-bold">Carpenter &amp; Company, CPAs, PC</div>
                    <div className="text-lg font-semibold mt-1">Payroll Report</div>
                    {selectedPeriod && (
                      <div className="text-sm text-gray-600 mt-1">
                        Pay Period: {periodLabel(selectedPeriod)} &nbsp;·&nbsp; Pay Date: {format(parseISO(selectedPeriod.pay_date), "MMMM d, yyyy")}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">Printed {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}</div>
                  </div>
                );
              })()}

              <div className="flex flex-wrap items-center gap-3 print:hidden">
                <span className="text-sm text-muted-foreground">Pay Period:</span>
                <Select value={payrollPeriodId} onValueChange={setPayrollPeriodId}>
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {payPeriods.map(p => (
                      <SelectItem key={p.id} value={p.id}>{periodLabel(p)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={printPayroll} disabled={payrollEntries.length === 0}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={exportPayrollCSV} disabled={payrollEntries.length === 0}>
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
              </div>

              {payrollLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : payrollEntries.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No time entries for this period.</CardContent></Card>
              ) : (
                <div className="space-y-6">
                  {Object.entries(payrollByUser).map(([uid, entries]) => {
                    const totalH = entries.reduce((s, e) => s + e.hours, 0);
                    const billableH = entries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
                    return (
                      <Card key={uid} className="shadow-sm print:shadow-none print:border print:break-before-auto print:mt-6">
                        <CardHeader className="pb-2 print:break-after-avoid">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{entries[0].user_name}</CardTitle>
                            <div className="text-sm text-muted-foreground font-mono">
                              {billableH.toFixed(1)}h billable &nbsp;/&nbsp; {totalH.toFixed(1)}h total
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-0">
                          {(() => {
                            const byDate = entries.reduce<Record<string, typeof entries>>((acc, e) => {
                              if (!acc[e.entry_date]) acc[e.entry_date] = [];
                              acc[e.entry_date].push(e);
                              return acc;
                            }, {});
                            return (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-muted/30 text-muted-foreground text-left">
                                    <th className="px-4 py-2 font-medium">Date</th>
                                    <th className="px-4 py-2 font-medium">Client</th>
                                    <th className="px-4 py-2 font-medium">Code</th>
                                    <th className="px-4 py-2 font-medium">Activity</th>
                                    <th className="px-4 py-2 font-medium text-right">Hours</th>
                                    <th className="px-4 py-2 font-medium">Bill</th>
                                    <th className="px-4 py-2 font-medium">Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(byDate).map(([date, dayEntries]) => {
                                    const dayTotal = dayEntries.reduce((s, e) => s + e.hours, 0);
                                    return [
                                      ...dayEntries.map((e, i) => (
                                        <tr key={e.id} className="border-b border-border/40 hover:bg-muted/20">
                                          <td className="px-4 py-2 font-mono whitespace-nowrap">
                                            {i === 0 ? format(parseISO(date), "EEE MM/dd") : ""}
                                          </td>
                                          <td className="px-4 py-2">{e.account_name || <span className="text-muted-foreground">—</span>}</td>
                                          <td className="px-4 py-2 font-mono text-xs">{e.activity_code}</td>
                                          <td className="px-4 py-2">{e.activity_name}</td>
                                          <td className="px-4 py-2 text-right font-mono font-semibold">{e.hours.toFixed(2)}</td>
                                          <td className="px-4 py-2">
                                            {e.billable
                                              ? <span className="text-xs font-medium text-emerald-600">●</span>
                                              : <span className="text-xs text-muted-foreground">○</span>}
                                          </td>
                                          <td className="px-4 py-2 text-muted-foreground max-w-[200px] truncate">{e.notes}</td>
                                        </tr>
                                      )),
                                      <tr key={`${date}-sub`} className="bg-muted/20 border-b border-border">
                                        <td colSpan={4} className="px-4 py-1 text-right text-xs text-muted-foreground italic">Day total</td>
                                        <td className="px-4 py-1 text-right font-mono text-xs font-semibold">{dayTotal.toFixed(2)}</td>
                                        <td colSpan={2} />
                                      </tr>
                                    ];
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr className="border-t bg-muted/30 font-semibold">
                                    <td colSpan={4} className="px-4 py-2 text-right text-muted-foreground">Period total</td>
                                    <td className="px-4 py-2 text-right font-mono">{totalH.toFixed(2)}</td>
                                    <td colSpan={2} />
                                  </tr>
                                </tfoot>
                              </table>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    );
                  })}

                  <Card className="shadow-sm border-primary/30">
                    <CardContent className="py-4">
                      <div className="flex justify-between items-center font-semibold">
                        <span>Period Total — All Staff</span>
                        <span className="font-mono text-lg">
                          {payrollEntries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0).toFixed(2)}h billable &nbsp;/&nbsp;
                          {payrollEntries.reduce((s, e) => s + e.hours, 0).toFixed(2)}h total
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          )}

          {/* ── EMPLOYEE REPORT TAB ── */}
          {isManager && (
            <TabsContent value="employee" className="space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-muted-foreground">Employee:</span>
                <Select value={empUserId} onValueChange={setEmpUserId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">Pay Period:</span>
                <Select value={empPeriodId} onValueChange={setEmpPeriodId}>
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {payPeriods.map(p => (
                      <SelectItem key={p.id} value={p.id}>{periodLabel(p)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => window.print()} disabled={empEntries.length === 0}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={exportEmpCSV} disabled={empEntries.length === 0}>
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
              </div>

              {!empUserId ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">Select an employee to view their timesheet.</CardContent></Card>
              ) : empLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : empEntries.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No time entries for this employee and period.</CardContent></Card>
              ) : (
                <div className="space-y-4">
                  {/* Summary metrics */}
                  {(() => {
                    const billH = empEntries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
                    const nonBillH = empEntries.filter(e => !e.billable).reduce((s, e) => s + e.hours, 0);
                    const totalH = billH + nonBillH;
                    const rate = totalH > 0 ? (billH / totalH) * 100 : 0;
                    return (
                      <div className="grid grid-cols-3 gap-4">
                        <MetricCard title="Billable Hours" value={billH.toFixed(1)} subValue="Hours" className="border-l-4 border-l-emerald-500" />
                        <MetricCard title="Total Hours" value={totalH.toFixed(1)} subValue="Hours" className="border-l-4 border-l-sidebar-primary" />
                        <MetricCard title="Utilization" value={`${rate.toFixed(1)}%`} className="border-l-4 border-l-primary" />
                      </div>
                    );
                  })()}

                  {/* Day-by-day breakdown */}
                  {(() => {
                    const byDate = empEntries.reduce<Record<string, TeamTimeEntry[]>>((acc, e) => {
                      if (!acc[e.entry_date]) acc[e.entry_date] = [];
                      acc[e.entry_date].push(e);
                      return acc;
                    }, {});
                    return (
                      <Card className="shadow-sm">
                        <CardContent className="p-0">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-muted/30 text-muted-foreground text-left">
                                <th className="px-4 py-2 font-medium">Date</th>
                                <th className="px-4 py-2 font-medium">Client</th>
                                <th className="px-4 py-2 font-medium">Code</th>
                                <th className="px-4 py-2 font-medium">Activity</th>
                                <th className="px-4 py-2 font-medium text-right">Hours</th>
                                <th className="px-4 py-2 font-medium">Bill</th>
                                <th className="px-4 py-2 font-medium">Notes</th>
                                <th className="px-2 py-2 font-medium w-16"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(byDate).map(([date, dayEntries]) => {
                                const dayTotal = dayEntries.reduce((s, e) => s + e.hours, 0);
                                return [
                                  ...dayEntries.map((e, i) => editingId === e.id ? (
                                    <tr key={e.id} className="border-b bg-blue-50/40 dark:bg-blue-950/20">
                                      <td className="px-2 py-1.5">
                                        <input type="date" value={editForm.entry_date}
                                          onChange={ev => setEditForm(f => ({ ...f, entry_date: ev.target.value }))}
                                          className="border rounded px-1.5 py-0.5 text-xs w-32" />
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <select value={editForm.account_id}
                                          onChange={ev => setEditForm(f => ({ ...f, account_id: ev.target.value }))}
                                          className="border rounded px-1.5 py-0.5 text-xs w-40">
                                          <option value="">— No client —</option>
                                          {editAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                        </select>
                                      </td>
                                      <td className="px-2 py-1.5" colSpan={2}>
                                        <select value={editForm.activity_code}
                                          onChange={ev => setEditForm(f => ({ ...f, activity_code: ev.target.value }))}
                                          className="border rounded px-1.5 py-0.5 text-xs w-56">
                                          {activityCodes.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                                        </select>
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <input type="number" step="0.25" min="0" max="24" value={editForm.hours}
                                          onChange={ev => setEditForm(f => ({ ...f, hours: ev.target.value }))}
                                          className="border rounded px-1.5 py-0.5 text-xs w-16 text-right" />
                                      </td>
                                      <td className="px-2 py-1.5 text-center">
                                        <input type="checkbox" checked={editForm.billable}
                                          onChange={ev => setEditForm(f => ({ ...f, billable: ev.target.checked }))} />
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <input type="text" value={editForm.notes}
                                          onChange={ev => setEditForm(f => ({ ...f, notes: ev.target.value }))}
                                          className="border rounded px-1.5 py-0.5 text-xs w-full" />
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <div className="flex items-center gap-1">
                                          <button onClick={saveEdit} disabled={empSaving}
                                            className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-50" title="Save">
                                            {empSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                          </button>
                                          <button onClick={cancelEdit}
                                            className="p-1 rounded text-muted-foreground hover:bg-muted" title="Cancel">
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ) : confirmDeleteId === e.id ? (
                                    <tr key={e.id} className="border-b bg-red-50/40 dark:bg-red-950/20">
                                      <td colSpan={7} className="px-4 py-2 text-sm text-red-700">
                                        Delete this entry ({e.hours.toFixed(2)}h — {e.account_name || "no client"})? This cannot be undone.
                                      </td>
                                      <td className="px-2 py-2">
                                        <div className="flex items-center gap-1">
                                          <button onClick={() => deleteEntry(e.id)} disabled={empDeleting === e.id}
                                            className="px-2 py-0.5 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs disabled:opacity-50">
                                            {empDeleting === e.id ? "…" : "Delete"}
                                          </button>
                                          <button onClick={() => setConfirmDeleteId(null)}
                                            className="px-2 py-0.5 rounded border text-xs hover:bg-muted">
                                            Cancel
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ) : (
                                    <tr key={e.id} className="border-b border-border/50 hover:bg-muted/20">
                                      <td className="px-4 py-2 font-mono whitespace-nowrap">
                                        {i === 0 ? format(parseISO(date), "EEE MM/dd") : ""}
                                      </td>
                                      <td className="px-4 py-2">{e.account_name || <span className="text-muted-foreground">—</span>}</td>
                                      <td className="px-4 py-2 font-mono text-xs">{e.activity_code}</td>
                                      <td className="px-4 py-2">{e.activity_name}</td>
                                      <td className="px-4 py-2 text-right font-mono font-semibold">{e.hours.toFixed(2)}</td>
                                      <td className="px-4 py-2">
                                        {e.billable
                                          ? <span className="text-xs font-medium text-emerald-600">●</span>
                                          : <span className="text-xs text-muted-foreground">○</span>}
                                      </td>
                                      <td className="px-4 py-2 text-muted-foreground max-w-[200px] truncate">{e.notes}</td>
                                      <td className="px-2 py-2">
                                        <div className="flex items-center gap-1">
                                          {e.status !== "approved" && (
                                            <button onClick={() => startEdit(e)}
                                              className="p-1 rounded text-blue-600 hover:bg-blue-50" title="Edit">
                                              <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                          {e.status === "draft" && (
                                            <button onClick={() => setConfirmDeleteId(e.id)}
                                              className="p-1 rounded text-rose-500 hover:bg-rose-50" title="Delete">
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )),
                                  <tr key={`${date}-total`} className="bg-muted/20 border-b">
                                    <td colSpan={4} className="px-4 py-1 text-right text-xs text-muted-foreground">Day total</td>
                                    <td className="px-4 py-1 text-right font-mono text-xs font-bold">{dayTotal.toFixed(2)}</td>
                                    <td colSpan={3} />
                                  </tr>
                                ];
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 bg-muted/40 font-bold">
                                <td colSpan={4} className="px-4 py-3 text-right">Period Total</td>
                                <td className="px-4 py-3 text-right font-mono text-base">
                                  {empEntries.reduce((s, e) => s + e.hours, 0).toFixed(2)}h
                                </td>
                                <td colSpan={3} />
                              </tr>
                            </tfoot>
                          </table>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
              )}
            </TabsContent>
          )}
          {/* ── BY CLIENT TAB ── */}
          {isManager && (
            <TabsContent value="by-client" className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {reportPresets.map(p => (
                  <Button key={p.label} variant="outline" size="sm"
                    onClick={() => { setReportDateFrom(format(p.from, "yyyy-MM-dd")); setReportDateTo(format(p.to, "yyyy-MM-dd")); }}>
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">From</span>
                  <input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">To</span>
                  <input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <Button variant="outline" onClick={printClientReport} disabled={clientGrouped.length === 0}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={exportClientCSV} disabled={clientGrouped.length === 0}>
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
              </div>

              {availableCategories.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 px-1">
                  <span className="text-xs font-medium text-muted-foreground shrink-0">Exclude:</span>
                  {availableCategories.map(cat => (
                    <label key={cat} className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={excludedCategories.has(cat)}
                        onChange={() => setExcludedCategories(prev => {
                          const next = new Set(prev);
                          next.has(cat) ? next.delete(cat) : next.add(cat);
                          return next;
                        })}
                        className="rounded"
                      />
                      <span className="text-xs capitalize">{cat}</span>
                    </label>
                  ))}
                  {excludedCategories.size > 0 && (
                    <button onClick={() => setExcludedCategories(new Set())} className="text-xs text-muted-foreground underline hover:text-foreground">
                      clear
                    </button>
                  )}
                </div>
              )}

              {reportLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : clientGrouped.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No time entries for this period.</CardContent></Card>
              ) : (
                <div className="space-y-1">
                  {/* Firm total summary */}
                  {(() => {
                    const grandTotal = filteredReportEntries.reduce((s, e) => s + e.hours, 0);
                    const grandBill = filteredReportEntries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
                    return (
                      <div className="flex items-center justify-between px-4 py-2 rounded-md bg-primary/5 border border-primary/20 text-sm font-semibold mb-3">
                        <span>{clientGrouped.length} clients</span>
                        <span className="font-mono">{grandBill.toFixed(1)}h billable &nbsp;/&nbsp; {grandTotal.toFixed(1)}h total</span>
                      </div>
                    );
                  })()}

                  {clientGrouped.map(c => {
                    const pct = c.totalHours > 0 ? Math.round((c.billableHours / c.totalHours) * 100) : 0;
                    const expanded = clientExpanded.has(c.accountId);
                    return (
                      <Card key={c.accountId} className="shadow-sm">
                        <CardContent className="p-0">
                          <div
                            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/20 rounded-lg select-none"
                            onClick={() => setClientExpanded(prev => {
                              const next = new Set(prev);
                              expanded ? next.delete(c.accountId) : next.add(c.accountId);
                              return next;
                            })}
                          >
                            {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                            <span className="font-medium flex-1 truncate">{c.accountName}</span>
                            <span className="text-xs text-muted-foreground">{Object.keys(c.employees).length} staff</span>
                            <span className="font-mono text-sm text-emerald-600 w-20 text-right">{c.billableHours.toFixed(1)}h bill</span>
                            <span className="font-mono text-sm w-20 text-right">{c.totalHours.toFixed(1)}h total</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full w-14 text-center ${pct >= 80 ? "bg-emerald-100 text-emerald-800" : pct >= 60 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                              {pct}%
                            </span>
                          </div>
                          {expanded && (
                            <div className="border-t mx-4 mb-3">
                              <table className="w-full text-sm mt-2">
                                <thead>
                                  <tr className="text-muted-foreground text-left">
                                    <th className="pb-1 pl-6 font-medium">Employee</th>
                                    <th className="pb-1 font-medium text-right">Billable</th>
                                    <th className="pb-1 font-medium text-right">Total</th>
                                    <th className="pb-1 font-medium text-right">Bill %</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border/40">
                                  {Object.values(c.employees).sort((a, b) => b.total - a.total).map(e => {
                                    const ep = e.total > 0 ? Math.round((e.billable / e.total) * 100) : 0;
                                    return (
                                      <tr key={e.name} className="hover:bg-muted/10">
                                        <td className="py-1.5 pl-6 text-muted-foreground">{e.name}</td>
                                        <td className="py-1.5 text-right font-mono text-emerald-600">{e.billable.toFixed(1)}h</td>
                                        <td className="py-1.5 text-right font-mono">{e.total.toFixed(1)}h</td>
                                        <td className="py-1.5 text-right font-mono text-muted-foreground">{ep}%</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}

          {/* ── DELEGATION TAB ── */}
          {isManager && (
            <TabsContent value="delegation" className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {reportPresets.map(p => (
                  <Button key={p.label} variant="outline" size="sm"
                    onClick={() => { setReportDateFrom(format(p.from, "yyyy-MM-dd")); setReportDateTo(format(p.to, "yyyy-MM-dd")); }}>
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">From</span>
                  <input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">To</span>
                  <input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">Also count as delegated</span>
                  <Select value="__picker__" onValueChange={id => {
                    if (id === "__picker__") return;
                    setDelegExtraUserIds(prev => {
                      const next = new Set(prev);
                      next.has(id) ? next.delete(id) : next.add(id);
                      return next;
                    });
                  }}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder={delegExtraUserIds.size > 0 ? `+${delegExtraUserIds.size} added` : "Add person…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {userRoles.filter(u => u.role !== "contractor").map(u => (
                        <SelectItem key={u.userId} value={u.userId}>
                          {delegExtraUserIds.has(u.userId) ? "✓ " : ""}{u.userName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {delegExtraUserIds.size > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setDelegExtraUserIds(new Set())}>Clear</Button>
                )}
                <Button variant="outline" onClick={printDelegationReport} disabled={delegGrouped.length === 0}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={exportDelegationCSV} disabled={delegGrouped.length === 0}>
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
              </div>

              {availableCategories.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 px-1">
                  <span className="text-xs font-medium text-muted-foreground shrink-0">Exclude:</span>
                  {availableCategories.map(cat => (
                    <label key={cat} className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input type="checkbox" checked={excludedCategories.has(cat)} className="rounded"
                        onChange={() => setExcludedCategories(prev => {
                          const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next;
                        })} />
                      <span className="text-xs capitalize">{cat}</span>
                    </label>
                  ))}
                  {excludedCategories.size > 0 && (
                    <button onClick={() => setExcludedCategories(new Set())} className="text-xs text-muted-foreground underline hover:text-foreground">clear</button>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                <strong>Delegated</strong> = contractor-role staff{delegExtraUserIds.size > 0 ? ` + ${delegExtraUserIds.size} added` : ""}. &nbsp;
                <strong>Senior</strong> = everyone else. Expand any client to see who logged what.
              </p>

              {reportLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : delegGrouped.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No time entries for this period.</CardContent></Card>
              ) : (
                <div className="space-y-1">
                  {/* Firm summary */}
                  {(() => {
                    const totSenior = delegGrouped.reduce((s, c) => s + c.seniorHours, 0);
                    const totDeleg = delegGrouped.reduce((s, c) => s + c.delegatedHours, 0);
                    const totAll = totSenior + totDeleg;
                    const pct = totAll > 0 ? Math.round((totDeleg / totAll) * 100) : 0;
                    return (
                      <div className="flex items-center justify-between px-4 py-2 rounded-md bg-violet-50 border border-violet-200 text-sm font-semibold mb-3">
                        <span>{delegGrouped.length} clients</span>
                        <span className="font-mono text-muted-foreground">
                          Senior: {totSenior.toFixed(1)}h &nbsp;·&nbsp; Delegated: {totDeleg.toFixed(1)}h &nbsp;·&nbsp;
                          <span className="text-violet-700">{pct}% delegated</span>
                        </span>
                      </div>
                    );
                  })()}

                  {delegGrouped.map(c => {
                    const delegPct = c.totalHours > 0 ? Math.round((c.delegatedHours / c.totalHours) * 100) : 0;
                    const expanded = delegExpanded.has(c.accountId);
                    return (
                      <Card key={c.accountId} className="shadow-sm">
                        <CardContent className="p-0">
                          <div
                            className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/20 rounded-lg select-none"
                            onClick={() => setDelegExpanded(prev => {
                              const next = new Set(prev);
                              expanded ? next.delete(c.accountId) : next.add(c.accountId);
                              return next;
                            })}
                          >
                            {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                            <span className="font-medium flex-1 truncate">{c.accountName}</span>
                            <span className="font-mono text-sm text-muted-foreground w-24 text-right">{c.seniorHours.toFixed(1)}h senior</span>
                            <span className="font-mono text-sm text-violet-600 w-28 text-right">{c.delegatedHours.toFixed(1)}h delegated</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full w-16 text-center ${delegPct >= 50 ? "bg-violet-100 text-violet-800" : delegPct >= 25 ? "bg-blue-100 text-blue-800" : "bg-muted text-muted-foreground"}`}>
                              {delegPct}% del
                            </span>
                          </div>
                          {expanded && (
                            <div className="border-t mx-4 mb-3">
                              <table className="w-full text-sm mt-2">
                                <thead>
                                  <tr className="text-muted-foreground text-left">
                                    <th className="pb-1 pl-6 font-medium">Staff Member</th>
                                    <th className="pb-1 font-medium text-right">Hours</th>
                                    <th className="pb-1 font-medium">Type</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border/40">
                                  {Object.values(c.people).sort((a, b) => b.hours - a.hours).map(p => (
                                    <tr key={p.name} className="hover:bg-muted/10">
                                      <td className="py-1.5 pl-6 text-muted-foreground">{p.name}</td>
                                      <td className="py-1.5 text-right font-mono">{p.hours.toFixed(1)}h</td>
                                      <td className="py-1.5 pl-3">
                                        {p.isDelegated
                                          ? <span className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">delegated</span>
                                          : <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">senior</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}

          {/* ── LEVERAGE TAB (admin only) ── */}
          {isAdmin && (
            <TabsContent value="leverage" className="space-y-4">
              {/* Rate configuration */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Billing Rate Configuration</CardTitle>
                  <CardDescription>Estimated billing rates per role. Saved to your browser. Update when rates change.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {(["admin", "manager", "staff", "contractor"] as const).map(role => (
                      <div key={role} className="flex flex-col gap-1">
                        <label className="text-xs font-medium capitalize text-muted-foreground">{role}</label>
                        <div className="flex items-center gap-1 h-9 rounded-md border border-input bg-background px-3 text-sm">
                          <span className="text-muted-foreground">$</span>
                          <input
                            type="number" min="0" step="5"
                            value={leverageRates[role] ?? defaultRates[role]}
                            onChange={e => {
                              const next = { ...leverageRates, [role]: parseFloat(e.target.value) || 0 };
                              setLeverageRates(next);
                              localStorage.setItem(LEVERAGE_RATES_KEY, JSON.stringify(next));
                            }}
                            className="flex-1 bg-transparent outline-none font-mono w-16"
                          />
                          <span className="text-muted-foreground text-xs">/hr</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-wrap items-center gap-2">
                {reportPresets.map(p => (
                  <Button key={p.label} variant="outline" size="sm"
                    onClick={() => { setReportDateFrom(format(p.from, "yyyy-MM-dd")); setReportDateTo(format(p.to, "yyyy-MM-dd")); }}>
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">From</span>
                  <input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground font-medium">To</span>
                  <input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </div>
                <Button variant="outline" onClick={printLeverageReport} disabled={leverageRows.length === 0}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={exportLeverageCSV} disabled={leverageRows.length === 0}>
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
              </div>

              {reportLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : leverageRows.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No utilization data for this period.</CardContent></Card>
              ) : (
                <>
                  {/* Firm summary metrics */}
                  {(() => {
                    const totalRev = leverageRows.reduce((s, r) => s + r.estRevenue, 0);
                    const totalBill = leverageRows.reduce((s, r) => s + r.billable_hours, 0);
                    const totalHrsAll = leverageRows.reduce((s, r) => s + r.total_hours, 0);
                    const effRate = totalHrsAll > 0 ? totalRev / totalHrsAll : 0;
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <MetricCard title="Est. Gross Revenue" value={`$${totalRev.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} className="border-l-4 border-l-emerald-500" />
                        <MetricCard title="Billable Hours" value={totalBill.toFixed(1)} subValue="Hours" className="border-l-4 border-l-primary" />
                        <MetricCard title="Effective Rate" value={`$${effRate.toFixed(0)}/hr`} subValue="Revenue ÷ total hours" className="border-l-4 border-l-amber-500" />
                      </div>
                    );
                  })()}

                  <Card className="shadow-sm">
                    <CardContent className="p-0">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30 text-muted-foreground text-left">
                            <th className="px-4 py-3 font-medium">Name</th>
                            <th className="px-4 py-3 font-medium">Role</th>
                            <th className="px-4 py-3 font-medium text-right">Bill. Hrs</th>
                            <th className="px-4 py-3 font-medium text-right">Rate</th>
                            <th className="px-4 py-3 font-medium text-right">Est. Revenue</th>
                            <th className="px-4 py-3 font-medium text-right">Non-Bill</th>
                            <th className="px-4 py-3 font-medium text-right">Total Hrs</th>
                            <th className="px-4 py-3 font-medium text-right">Eff. Rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {leverageRows.map(r => (
                            <tr key={r.user_id} className="hover:bg-muted/20">
                              <td className="px-4 py-3 font-medium">{r.name}</td>
                              <td className="px-4 py-3 capitalize text-muted-foreground">{r.role}</td>
                              <td className="px-4 py-3 text-right font-mono">{r.billable_hours.toFixed(1)}h</td>
                              <td className="px-4 py-3 text-right font-mono text-muted-foreground">${r.rate}/hr</td>
                              <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">
                                ${r.estRevenue.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </td>
                              <td className="px-4 py-3 text-right font-mono text-muted-foreground">{r.non_billable_hours.toFixed(1)}h</td>
                              <td className="px-4 py-3 text-right font-mono">{r.total_hours.toFixed(1)}h</td>
                              <td className="px-4 py-3 text-right font-mono text-muted-foreground">${r.effectiveRate.toFixed(0)}/hr</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 bg-muted/40 font-semibold">
                            <td colSpan={2} className="px-4 py-3">Firm Total</td>
                            <td className="px-4 py-3 text-right font-mono">{leverageRows.reduce((s, r) => s + r.billable_hours, 0).toFixed(1)}h</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                            <td className="px-4 py-3 text-right font-mono text-emerald-600">
                              ${leverageRows.reduce((s, r) => s + r.estRevenue, 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </td>
                            <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                            <td className="px-4 py-3 text-right font-mono">{leverageRows.reduce((s, r) => s + r.total_hours, 0).toFixed(1)}h</td>
                            <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                              {(() => {
                                const rev = leverageRows.reduce((s, r) => s + r.estRevenue, 0);
                                const hrs = leverageRows.reduce((s, r) => s + r.total_hours, 0);
                                return hrs > 0 ? `$${(rev / hrs).toFixed(0)}/hr` : "—";
                              })()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </CardContent>
                  </Card>
                  <p className="text-xs text-muted-foreground">
                    * Revenue estimates based on configured billing rates above. Actual billed amounts may differ. Connect billing data for actuals.
                  </p>
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
